import {
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import alqemiDataService, {
  Match,
  ProjectIdentifier,
} from "../../../api/AlqemiDataService";
import alqemiService from "../../../api/AlqemiService";
import featureFlagsService from "../../../api/FeatureFlagsService";
import feedbackService from "../../../api/FeedbackService";
import Icon, { BiggerIcon } from "../../../common/components/fundamentals/Icon";
import { usePopUp } from "../../../common/context/PopUpProvider";
import { useSnackbar } from "../../../common/context/SnackbarProvider";
import { useUser } from "../../../common/context/UserProvider";
import { FullStack } from "../../../common/styles/Box.styles";
import { TaskStatusIcon } from "../../../common/styles/Form.styles";
import { MediumText, SmallText } from "../../../common/styles/Text.styles";
import CVPDF from "../../../cv-generation/CVPDF";
import {
  borderStyle,
  defaultTransition,
  iconGap,
  perelynBlack,
  perelynDarkBlue,
  perelynLightBlue,
  perelynPrimary,
  smallerGap,
  smallGap,
  tinyGap,
} from "../../../styles/consts";
import {
  hasSalesOrAdminAccess,
  isSupervisorFlowActive,
  replaceWhitespaceForDash,
} from "../../../utils";
import PopUpBox from "../../components/PopUpBox";
import { useOffer } from "../../context/OfferProvider";
import { useStepNavigation } from "../../context/StepNavigationContext";
import {
  isConfirmed as checkIsConfirmed,
  isNotConfirmed,
} from "../../utils/confirmationUtils";
import { applyConsultantWorklistFilters } from "../../utils/dashboardFilters";
import { substituteHourlyRate } from "../../utils/substituteHourlyRate";
import AlqemiFeedback from "../AlqemiFeedback";
import CandidateSelectionRow from "../CandidateSelectionRow";
import SimilarProjectsDialog from "../Components/SimilarProjectsDialog";
import RejectionPopupContent from "../RejectionPopupContent";
import StepNavigation from "../StepNavigation";

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve((reader.result as string).split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

export default function FeedbackStep() {
  const { offer, setOffer } = useOffer();
  const { user } = useUser();
  const { showSnackbar } = useSnackbar();
  const { showPopUpWindow } = usePopUp();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { offerId } = useParams();
  const { setCandidateStep } = useStepNavigation();

  const [displayedMatchEmail, setDisplayedMatchEmail] = useState("");
  const [commentsRefreshTrigger, setCommentsRefreshTrigger] = useState(0);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [closeRejectPopUp, setCloseRejectPopUp] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRejected, setIsRejected] = useState(false);
  const [pdfDownloadBlob, setPdfDownloadBlob] = useState<Blob | null>(null);
  const [closeFeedbackPopUp, setCloseFeedbackPopUp] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [isSendingFeedback, setIsSendingFeedback] = useState(false);
  const [isMarkingAsSent, setIsMarkingAsSent] = useState(false);
  const [isMarkedAsSent, setIsMarkedAsSent] = useState(false);
  const [isSimilarProjectsDialogOpen, setIsSimilarProjectsDialogOpen] =
    useState(false);
  const [manualRecruiterEmail, setManualRecruiterEmail] = useState<string>("");
  const [isSendingApplication, setIsSendingApplication] = useState(false);
  const [applicationSent, setApplicationSent] = useState(false);
  const sendingRef = useRef(false);
  const [supervisorFlagLive, setSupervisorFlagLive] = useState<boolean>(true);
  const [autoSendFlagLive, setAutoSendFlagLive] = useState<boolean>(false);
  useEffect(() => {
    featureFlagsService
      .listFlags()
      .then((flags) => {
        const flag = flags.find((f) => f.name === "supervisor_enabled");
        if (flag !== undefined) {
          setSupervisorFlagLive(flag.enabled);
        }
        const autoSend = flags.find((f) => f.name === "auto_send_enabled");
        if (autoSend !== undefined) {
          setAutoSendFlagLive(autoSend.enabled);
        }
      })
      .catch(() => {
        // Keep fail-safe defaults (supervisor true, auto-send false).
      });
  }, []);

  const setCurrentCandidateInUrl = (email: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("currentCandidate", email);
    window.history.replaceState(null, "", `?${newSearchParams.toString()}`);
  };

  useEffect(() => {
    const currentCandidate = searchParams.get("currentCandidate");

    const candidateExists =
      currentCandidate &&
      offer?.matches?.find(
        (match) => match.candidate?.email === currentCandidate,
      );

    if (candidateExists) {
      if (currentCandidate !== displayedMatchEmail) {
        setDisplayedMatchEmail(currentCandidate);
      }
    } else if (offer?.matches && offer.matches.length > 0) {
      const highestScoreMatch = offer.matches.find(
        (match) =>
          match.score ===
          Math.max(...(offer.matches?.map((m) => m.score || 0) || [])),
      );
      const defaultEmail =
        highestScoreMatch?.candidate?.email ||
        offer.matches[0]?.candidate?.email ||
        "";
      if (defaultEmail) {
        setDisplayedMatchEmail(defaultEmail);
        setCurrentCandidateInUrl(defaultEmail);
      }
    }
  }, [searchParams, offer?.matches]);

  const displayedMatch = offer?.matches?.find(
    (match) => match.candidate?.email === displayedMatchEmail,
  );

  // Prefill the recruiter-email box with the auto-resolved address so sales
  // can correct it in place.
  useEffect(() => {
    const resolved = displayedMatch?.recruiter_email;
    if (resolved && !manualRecruiterEmail) {
      setManualRecruiterEmail(resolved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedMatch?.recruiter_email]);

  // Polling for re-fetching the recruiter email AFTER email generation without reload
  const [recruiterPolls, setRecruiterPolls] = useState(0);
  useEffect(() => {
    setRecruiterPolls(0);
  }, [displayedMatch?.id]);

  const recruiterPollActive =
    !!displayedMatch?.id &&
    !!displayedMatch?.email &&
    !displayedMatch?.recruiter_email &&
    recruiterPolls < 5;

  useQuery({
    queryKey: [
      "feedback-recruiter-email",
      displayedMatch?.id,
      displayedMatch?.recruiter_email,
    ],
    queryFn: async () => {
      if (!displayedMatch?.id) return null;
      setRecruiterPolls((n) => n + 1);
      const result = await alqemiDataService.getMatch(displayedMatch.id);
      const fresh = result?.data;
      if (fresh?.recruiter_email && offer && setOffer) {
        setOffer({
          ...offer,
          matches: offer.matches?.map((match) =>
            match.id === displayedMatch.id
              ? {
                  ...match,
                  recruiter_email: fresh.recruiter_email,
                  recruiter_email_source: fresh.recruiter_email_source,
                  recruiter_email_verification:
                    fresh.recruiter_email_verification,
                  recruiter_email_pattern_method:
                    fresh.recruiter_email_pattern_method,
                  recruiter_email_pattern_sample_count:
                    fresh.recruiter_email_pattern_sample_count,
                  email: fresh.email ?? match.email,
                  status: fresh.status ?? match.status,
                  updated_at: fresh.updated_at ?? match.updated_at,
                }
              : match,
          ),
        });
      }
      return fresh ?? null;
    },
    enabled: recruiterPollActive,
    refetchInterval: recruiterPollActive ? 3000 : false,
    retry: false,
  });

  const emailContent = substituteHourlyRate(
    displayedMatch?.email || "",
    displayedMatch?.candidate?.rates,
  );
  // Recruiter CV: drop the internal requirements-match table; memoized so the
  // hidden CVPDF only re-renders when the CV changes.
  const cvContent = useMemo(() => {
    const cv = { ...(displayedMatch?.cv || {}) };
    delete (cv as any).requirementsMatch;
    return cv;
  }, [displayedMatch?.cv]);
  const language = displayedMatch?.cv_language || "en";

  const isCandidate = displayedMatch?.candidate
    ? user.email === displayedMatch.candidate.email
    : false;
  const isSupervisor = displayedMatch?.candidate
    ? user.email === displayedMatch.candidate.supervisor_email
    : false;
  const hasSalesView = hasSalesOrAdminAccess(user) && !isCandidate;
  // Snapshot-driven: was the supervisor flow on at the time this match was
  // confirmed? If it was off, feedback from sales goes to the consultant.
  const supervisorFlowActive = isSupervisorFlowActive(displayedMatch);

  useEffect(() => {
    if (
      displayedMatch?.candidate &&
      !isCandidate &&
      !isSupervisor &&
      !hasSalesOrAdminAccess(user)
    ) {
      navigate(
        `/alqemi/${offerId}/review?currentCandidate=${encodeURIComponent(displayedMatchEmail)}`,
      );
    }
  }, [
    displayedMatch,
    isCandidate,
    isSupervisor,
    user.sales,
    user.admin,
    navigate,
    offerId,
    displayedMatchEmail,
  ]);

  const isAlreadyConfirmedBySupervisor = checkIsConfirmed(
    displayedMatch?.supervisor_confirmed,
  );
  const isAlreadyConfirmedByConsultant = checkIsConfirmed(
    displayedMatch?.consultant_confirmed,
  );
  const isConsultantYetToConfirm = isNotConfirmed(
    displayedMatch?.consultant_confirmed,
  );
  const isSupervisorYetToConfirm = isNotConfirmed(
    displayedMatch?.supervisor_confirmed,
  );

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(emailContent.toString());
    showSnackbar("Copied to clipboard!");
  };

  const handleDownloadCV = () => {
    if (pdfDownloadBlob) {
      const aTag = document.createElement("a");
      aTag.href = URL.createObjectURL(pdfDownloadBlob);
      aTag.style.display = "none";
      aTag.download = `Perelyn_Profile_${replaceWhitespaceForDash(
        displayedMatch?.candidate?.name || "Unknown",
      )}_${language.toUpperCase()}.pdf`;
      document.body.appendChild(aTag);
      aTag.click();
    }
  };

  const handleMarkAsSent = async () => {
    if (isMarkingAsSent) return;

    const matchId = displayedMatch?.id || displayedMatch?.consultant_id;
    if (matchId) {
      setIsMarkingAsSent(true);
      try {
        const result = await alqemiDataService.markMatchAsSent(matchId);

        setIsMarkedAsSent(true);
        if (result?.pipedrive_deal_id) {
          showSnackbar(`Offer marked as "Sent" and synced to Pipedrive!`);
        } else {
          showSnackbar(`Offer marked as "Sent"!`);
        }
      } finally {
        setIsMarkingAsSent(false);
      }
    }
  };

  useEffect(() => {
    if (displayedMatch) {
      const isAlreadyConfirmed =
        (isCandidate &&
          checkIsConfirmed(displayedMatch?.consultant_confirmed)) ||
        (isSupervisor &&
          checkIsConfirmed(displayedMatch?.supervisor_confirmed)) ||
        displayedMatch?.status === "rejected";

      setIsConfirmed(isAlreadyConfirmed);
      setIsRejected(displayedMatch?.status === "rejected");
      setIsMarkedAsSent(displayedMatch?.status === "sent");
    }
  }, [displayedMatch, user.email, isCandidate, isSupervisor]);

  const getFeedbackButtonText = () => {
    if (hasSalesView && !isSupervisor) {
      return "Send to Candidate";
    }
    const baseText = "Send";
    // Supervisor always routes to candidate. When supervisor flow was OFF at
    // confirmation, sales feedback goes straight to consultant (mirrors backend).
    const recipient = isSupervisor
      ? "candidate"
      : !supervisorFlowActive && hasSalesView
        ? "candidate"
        : hasSalesView
          ? "supervisor"
          : "candidate";
    const needsBackText = !(
      (isSupervisor && isConsultantYetToConfirm) ||
      (hasSalesView && isSupervisorYetToConfirm)
    );

    return `${baseText}${needsBackText ? " back" : ""} to ${recipient}`;
  };

  const handleSendFeedback = async () => {
    setIsSendingFeedback(true);
    setIsConfirmed(true);

    const jobTitle = offer.title;
    const matchId = displayedMatch?.id || displayedMatch?.consultant_id;

    if (hasSalesView) {
      await alqemiService.sendAlqemiEmail({
        displayedMatch,
        isSupervisor,
        jobTitle,
        feedback,
        isSales: !isSupervisor,
      });
      if (matchId) {
        await feedbackService.createFeedback({
          matchId: matchId,
          feedback: feedback,
          offerUrl: window.location.href,
        });
      }
      if (isSupervisor && matchId) {
        await alqemiDataService.updateMatch(
          matchId,
          "consultant_confirmed",
          false,
        );
      }
      showSnackbar(
        isSupervisor
          ? "Feedback sent to candidate"
          : supervisorFlowActive
            ? "Feedback sent to supervisor"
            : "Feedback sent to candidate",
      );
    } else {
      await alqemiService.sendAlqemiEmail({
        displayedMatch,
        isSupervisor,
        jobTitle,
        feedback,
        isSales: hasSalesView,
      });
      if (matchId) {
        await feedbackService.createFeedback({
          matchId: matchId,
          feedback: feedback,
          offerUrl: window.location.href,
        });
        await alqemiDataService.updateMatch(
          matchId,
          "consultant_confirmed",
          false,
        );
      }
      showSnackbar("Feedback sent to candidate");
    }
    setCloseFeedbackPopUp(true);
    setFeedback("");
    setIsSendingFeedback(false);
  };

  const handlePrevious = () => {
    const isCurrentUserCandidate = displayedMatch?.candidate
      ? user.email === displayedMatch.candidate.email
      : false;

    if (isCurrentUserCandidate) {
      setCandidateStep(displayedMatchEmail, 2);
      navigate(
        `/alqemi/${offerId}/cv-adaptation?currentCandidate=${encodeURIComponent(displayedMatchEmail)}`,
      );
    } else {
      setCandidateStep(displayedMatchEmail, 2);
      navigate(
        `/alqemi/${offerId}/email-review?currentCandidate=${encodeURIComponent(displayedMatchEmail)}`,
      );
    }
  };

  const handleCandidateClick = (email: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("currentCandidate", email);
    window.history.replaceState(null, "", `?${newSearchParams.toString()}`);
    setDisplayedMatchEmail(email);
  };

  const handleSubmitOffer = async () => {
    if (!displayedMatch) return;

    const jobTitle = offer.title;

    if (hasSalesView && !isSupervisor) {
      if (sendingRef.current) return;
      if (!pdfDownloadBlob) {
        showSnackbar("Cannot send email: CV PDF is not available", true);
        return;
      }

      const matchId = displayedMatch?.id || displayedMatch?.consultant_id;
      if (!matchId) {
        showSnackbar("Internal error: match id missing", true);
        return;
      }

      const resolvedEmail = displayedMatch?.recruiter_email ?? null;
      // The box is prefilled with the resolved address; it only counts as a
      // manual override when sales actually changed it (keeps the origin
      // tracking in the application log truthful).
      const typedEmail = manualRecruiterEmail.trim();
      const manualOverride =
        typedEmail && typedEmail !== resolvedEmail ? typedEmail : undefined;
      if (!resolvedEmail && !manualOverride) {
        showSnackbar(
          "No recruiter email available — please enter one manually.",
          true,
        );
        return;
      }

      sendingRef.current = true;
      setIsSendingApplication(true);
      const reader = new FileReader();
      reader.readAsDataURL(pdfDownloadBlob);
      reader.onloadend = async () => {
        const base64data = reader.result as string;
        const base64Content = base64data.split(",")[1];
        const candidateName = replaceWhitespaceForDash(
          displayedMatch?.candidate?.name || "Unknown",
        );

        try {
          await alqemiService.sendRecruiterEmail({
            matchId,
            emailContent,
            cv: base64Content,
            jobTitle,
            candidateName,
            manualEmailOverride: manualOverride,
          });
          showSnackbar(`Email sent successfully!`);
          setIsSendingApplication(false);
          setApplicationSent(true);
        } catch (err: any) {
          sendingRef.current = false;
          setIsSendingApplication(false);
          if (err?.response?.status === 422) {
            showSnackbar(
              "Backend could not resolve a recruiter email — please enter one manually.",
              true,
            );
          } else {
            showSnackbar("Send failed. Please retry.", true);
          }
        }
      };
      return;
    }

    const isAlreadyConfirmedByConsultant = checkIsConfirmed(
      displayedMatch?.consultant_confirmed,
    );
    const isAlreadyConfirmedBySupervisor = checkIsConfirmed(
      displayedMatch?.supervisor_confirmed,
    );
    const isRejected = displayedMatch?.status === "rejected";
    const isConsultantYetToConfirm = !isAlreadyConfirmedByConsultant;

    if (isCandidate && (isAlreadyConfirmedByConsultant || isRejected)) {
      showSnackbar("CV already confirmed or rejected");
      return;
    }
    if (isSupervisor && (isAlreadyConfirmedBySupervisor || isRejected)) {
      showSnackbar("CV already confirmed or rejected");
      return;
    }

    if (isSupervisor && isConsultantYetToConfirm) {
      showSnackbar("Waiting for consultant to confirm CV first");
      return;
    }

    const handleSupervisorConfirmCV = async () => {
      showSnackbar(
        "CV confirmed! Notification has been sent to the sales team",
      );
      const matchId = displayedMatch?.id || displayedMatch?.consultant_id;
      if (matchId) {
        await alqemiDataService.confirmMatch(matchId, true, true);
      }
      await alqemiService.sendAlqemiEmail({
        displayedMatch,
        isSupervisor: true,
        jobTitle,
      });
      setIsConfirmed(true);
    };

    if (isCandidate) {
      setIsSimilarProjectsDialogOpen(true);
      return;
    }

    showPopUpWindow(
      () => handleSupervisorConfirmCV(),
      `Confirm CV`,
      `Are you sure you want to confirm this CV?`,
      "Cancel",
      "Confirm",
    );
  };

  const handleRejectOffer = async (
    selectedReasons: string[],
    freeText: string,
  ) => {
    setIsLoading(true);
    try {
      let rejectionReason = "";
      if (selectedReasons.length > 0) {
        rejectionReason = selectedReasons.join(", ");
      }
      if (freeText.trim() !== "") {
        if (rejectionReason !== "") {
          rejectionReason += "\n\nAdditional comments: " + freeText.trim();
        } else {
          rejectionReason = freeText.trim();
        }
      }

      const jobTitle = offer.title;
      await alqemiService.sendRejectionEmail({
        displayedMatch,
        rejectionReason,
        jobTitle,
      });

      const matchId = displayedMatch?.id || displayedMatch?.consultant_id;
      if (matchId) {
        await alqemiDataService.rejectMatch(matchId);
      }

      setIsRejected(true);
      setCloseRejectPopUp(true);
      showSnackbar("Offer successfully rejected");

      const updatedOffer = { ...offer };
      if (updatedOffer.matches) {
        const matchIndex = updatedOffer.matches.findIndex(
          (m) => m.candidate?.email === displayedMatchEmail,
        );
        if (matchIndex !== -1) {
          updatedOffer.matches[matchIndex].status = "rejected";
          setOffer(updatedOffer);
        }
      }

      if (isCandidate && displayedMatch?.consultant_id) {
        applyConsultantWorklistFilters(displayedMatch.consultant_id);
        navigate("/alqemi");
      }
    } catch (error) {
      console.error("Error rejecting offer:", error);
      showSnackbar("Failed to reject offer. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const runCandidateConfirm = async () => {
    if (!displayedMatch?.cv || Object.keys(displayedMatch.cv).length === 0) {
      showSnackbar("You need to generate CV in order to submit it", true);
      throw new Error("CV is empty");
    }
    setIsConfirmed(true);
    try {
      const matchId = displayedMatch?.id || displayedMatch?.consultant_id;
      if (matchId) {
        let cvPdf: string | undefined;
        if (pdfDownloadBlob) {
          try {
            cvPdf = await blobToBase64(pdfDownloadBlob);
          } catch {
            cvPdf = undefined;
          }
        }
        await alqemiDataService.confirmMatch(matchId, false, true, cvPdf);
      }
      showSnackbar(
        autoSendFlagLive
          ? "CV submitted successfully!"
          : supervisorFlagLive
            ? "CV confirmed! Notification has been sent to your supervisor"
            : "CV confirmed! Notification has been sent to the sales team",
      );
      if (isCandidate && displayedMatch?.consultant_id) {
        applyConsultantWorklistFilters(displayedMatch.consultant_id);
        navigate("/alqemi");
      }
    } catch (error) {
      setIsConfirmed(false);
      showSnackbar("Failed to confirm CV. Please try again.", true);
      throw error;
    }
  };

  const handleSimilarProjectsSubmit = async (selected: ProjectIdentifier[]) => {
    const matchId = displayedMatch?.id || displayedMatch?.consultant_id;
    if (!matchId) {
      showSnackbar("No match selected", true);
      throw new Error("no match id");
    }
    if (!displayedMatch?.cv || Object.keys(displayedMatch.cv).length === 0) {
      showSnackbar("You need to generate CV in order to submit it", true);
      throw new Error("CV is empty");
    }
    try {
      await alqemiDataService.updateMatch(
        matchId,
        "selected_similar_projects",
        selected,
      );
    } catch (err) {
      showSnackbar(
        "Failed to save your project selection. Please try again.",
        true,
      );
      throw err;
    }
    await runCandidateConfirm();
    setIsSimilarProjectsDialogOpen(false);
  };

  return (
    <FullStack
      sx={{ display: "flex", flexDirection: "column", overflow: "hidden" }}
    >
      <Stack
        sx={{ padding: smallGap, gap: smallGap, overflow: "auto", flex: 1 }}
      >
        <CandidateSelectionRow
          matches={offer?.matches}
          displayedMatchEmail={displayedMatchEmail}
          onCandidateClick={handleCandidateClick}
          showDetailedScores={hasSalesOrAdminAccess(user)}
        />
        <AlqemiFeedback
          displayedMatchId={displayedMatch?.id || displayedMatch?.consultant_id}
          onFeedbackSubmitted={() =>
            setCommentsRefreshTrigger((prev) => prev + 1)
          }
          refreshTrigger={commentsRefreshTrigger}
        />

        {/* Hidden CVPDF component to generate the blob */}
        <div style={{ display: "none" }}>
          <CVPDF
            cvContent={cvContent}
            setPdfDownloadBlob={setPdfDownloadBlob}
            language={language}
          />
        </div>

        <Stack
          sx={{
            flexDirection: "row",
          }}
        >
          {displayedMatch?.candidate &&
            user.email === displayedMatch.candidate.email && (
              <Stack sx={{ paddingRight: smallGap }}>
                <PopUpBox
                  heading="Reject Offer"
                  button={
                    <Button
                      variant="outlined"
                      disabled={
                        checkIsConfirmed(
                          displayedMatch?.consultant_confirmed,
                        ) || isRejected
                      }
                      sx={{
                        ...(isRejected && {
                          borderColor: perelynPrimary,
                          color: perelynPrimary,
                        }),
                        gap: iconGap,
                        "&:hover": {
                          "& > *": {
                            transition: defaultTransition,
                          },
                        },
                      }}
                    >
                      {isRejected ? "Offer Rejected" : "Reject Offer"}
                      <BiggerIcon name="XMark" />
                    </Button>
                  }
                  width="50%"
                  closePopUp={closeRejectPopUp}
                  setClosePopUp={setCloseRejectPopUp}
                >
                  <RejectionPopupContent
                    onSubmit={handleRejectOffer}
                    onCancel={() => setCloseRejectPopUp(true)}
                    isLoading={isLoading}
                  />
                </PopUpBox>
              </Stack>
            )}

          <Stack sx={{ flexDirection: "row", gap: smallGap }}>
            <Button
              variant="outlined"
              id="download-cv"
              onClick={handleDownloadCV}
              disabled={!pdfDownloadBlob}
              sx={{
                padding: tinyGap,
                gap: "0.5rem",
              }}
            >
              CV
              <Icon
                sx={{
                  height: "1.5rem",
                  width: "1.5rem",
                }}
                name="Download"
              />
            </Button>
            {(hasSalesView || isSupervisor) && (
              <Button variant="outlined" onClick={handleCopyToClipboard}>
                <BiggerIcon name="Copy" />
              </Button>
            )}

            {hasSalesView && (
              <Button
                id="mark-as-sent"
                variant="outlined"
                onClick={handleMarkAsSent}
                disabled={
                  isMarkingAsSent ||
                  isMarkedAsSent ||
                  displayedMatch?.status === "sent"
                }
                sx={{
                  gap: iconGap,
                  "&:hover": {
                    "& .MuiStack-root": {
                      filter: "none",
                    },
                    "& .MuiStack-root .MuiTypography-root": {
                      filter: "none",
                    },
                  },
                }}
              >
                {isMarkingAsSent ? (
                  <CircularProgress size={20} />
                ) : (
                  <>
                    <Typography>Mark as</Typography>
                    <TaskStatusIcon
                      sx={{
                        backgroundColor: perelynLightBlue,
                        opacity: 0.9,
                      }}
                    >
                      <SmallText sx={{ color: perelynDarkBlue }}>
                        Sent
                      </SmallText>
                    </TaskStatusIcon>
                  </>
                )}
              </Button>
            )}
          </Stack>
        </Stack>

        <Stack sx={{ flexDirection: "column", gap: smallGap }}>
          {hasSalesView && !isSupervisor && (
            <Box sx={{ mb: 1.5 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Enter recruiter email"
                helperText={
                  displayedMatch?.recruiter_email
                    ? renderRecruiterEmailProvenance(displayedMatch)
                    : !displayedMatch?.email
                    ? "No email inferred yet — generate the email first (Email Review step) or enter one manually. If you did that already and it's still empty, refresh this page."
                    : "No recruiter email could be derived. Please enter one to send."
                }
                value={manualRecruiterEmail}
                onChange={(e) => setManualRecruiterEmail(e.target.value)}
              />
            </Box>
          )}
          {(isSupervisor || hasSalesView) && (
            <PopUpBox
              heading="Feedback"
              button={
                <Button
                  id="send-feedback"
                  variant="outlined"
                  disabled={
                    !hasSalesView &&
                    (isAlreadyConfirmedBySupervisor || isConfirmed)
                  }
                  sx={{
                    width: "100%",
                    gap: iconGap,
                  }}
                >
                  {getFeedbackButtonText()}
                  <BiggerIcon name="FlipBackward" />
                </Button>
              }
              width="90%"
              maxHeight="90%"
              closePopUp={closeFeedbackPopUp}
              setClosePopUp={setCloseFeedbackPopUp}
            >
              <Stack
                sx={{
                  border: borderStyle,
                  padding: smallGap,
                  gap: smallGap,
                }}
              >
                <Stack
                  sx={{
                    flexDirection: "row",
                    width: "100%",
                    gap: smallGap,
                    boxSizing: "border-box",
                  }}
                >
                  <Stack sx={{ width: "100%" }}>
                    <MediumText>Describe what should be improved</MediumText>
                    <TextField
                      id="email-template"
                      placeholder="Write here..."
                      multiline
                      minRows={10}
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                    />
                  </Stack>
                </Stack>
                <Stack
                  sx={{
                    flexDirection: "row",
                    justifyContent: "end",
                  }}
                >
                  <Button
                    variant="primary"
                    sx={{
                      "&&": { width: "8rem" },
                      ":hover": {
                        "& .MuiCircularProgress-root": {
                          color: perelynPrimary,
                        },
                      },
                    }}
                    onClick={handleSendFeedback}
                    disabled={!feedback || feedback.trim() === ""}
                  >
                    {isSendingFeedback ? (
                      <CircularProgress
                        sx={{ color: perelynBlack }}
                        size={smallerGap}
                      />
                    ) : (
                      "Send Feedback"
                    )}
                  </Button>
                </Stack>
              </Stack>
            </PopUpBox>
          )}
        </Stack>
      </Stack>

      <StepNavigation
        candidateEmail={displayedMatchEmail}
        onNext={handleSubmitOffer}
        onPrevious={handlePrevious}
        nextLabel={(() => {
          return hasSalesView && !(isSupervisor && user.admin)
            ? "Send out application"
            : "Submit CV";
        })()}
        nextDisabled={(() => {
          if (hasSalesView && !(isSupervisor && user.admin)) {
            if (isSendingApplication || applicationSent) return true;
            const sendable = displayedMatch?.status === "approved";
            const hasRecipient =
              Boolean(displayedMatch?.recruiter_email) ||
              manualRecruiterEmail.trim().length > 0;
            return !(sendable && hasRecipient);
          }

          if (!displayedMatch) return true;

          const localIsConsultantYetToConfirm = !isAlreadyConfirmedByConsultant;

          if (isCandidate) {
            return isAlreadyConfirmedByConsultant || isRejected || isConfirmed;
          }

          if (isSupervisor) {
            return (
              isAlreadyConfirmedBySupervisor ||
              isRejected ||
              localIsConsultantYetToConfirm ||
              isConfirmed
            );
          }

          return true;
        })()}
        nextLoading={isSendingApplication}
      />
      {displayedMatch && (
        <SimilarProjectsDialog
          open={isSimilarProjectsDialogOpen}
          onClose={() => setIsSimilarProjectsDialogOpen(false)}
          onSubmit={handleSimilarProjectsSubmit}
          projects={(displayedMatch.cv?.projects ?? []) as any[]}
          initialSelection={displayedMatch.selected_similar_projects ?? []}
        />
      )}
    </FullStack>
  );
}

// How the prefilled recruiter address was resolved — shown as helper text
// under the email input, so the address itself is not repeated here.
function renderRecruiterEmailProvenance(m: Match): string {
  const source = m.recruiter_email_source;
  const verification = m.recruiter_email_verification;

  let base: string;
  if (source === "job_description") {
    base = "Found in job description";
  } else if (source === "pipedrive_direct_match" || source === "pipedrive") {
    base = "Matched in Pipedrive";
  } else if (source === "pipedrive_indirect_match") {
    base = "Matched in Pipedrive (via pattern)";
  } else if (source === "pattern_derived") {
    const method = m.recruiter_email_pattern_method;
    const n = m.recruiter_email_pattern_sample_count;
    const contacts =
      n != null
        ? `${n} Pipedrive contact${n === 1 ? "" : "s"}`
        : "Pipedrive contacts";
    if (method === "runtime_llm") {
      base = `AI-guessed from ${contacts}`;
    } else if (
      method === "bootstrap_statistical" ||
      method === "runtime_statistical"
    ) {
      base = `Company email pattern (from ${contacts})`;
    } else {
      base = "Derived from company pattern";
    }
  } else {
    base = "Resolved automatically";
  }

  if (source !== "pattern_derived") {
    return base;
  }
  // DynamoDB persists null as the JSON value `null`, which deserialises to
  // `null` (not `undefined`) in TypeScript. The == check below catches both.
  if (verification == null) return base;
  switch (verification) {
    case "deliverable":
      return `${base} · verified ✓`;
    case "risky":
      return `${base} · accept-all domain (may bounce)`;
    case "undeliverable":
      return `${base} · ⚠ Hunter says this address is undeliverable`;
    case "unverified":
      return `${base} · could not verify (Hunter unavailable)`;
    default:
      return base;
  }
}
